import { Box, MenuItem, Popover, Typography, useTheme } from '@mui/material'
import { GridRenderEditCellParams } from '@mui/x-data-grid-premium'
import { useEffect, useState } from 'react'
import type { LeadScoreDA, LeadScoreIndustry } from 'types/approvers'

// Y-axis options (DA/Keyword Rankings)
export const daOptions: { value: LeadScoreDA; label: string }[] = [
  { value: 'Horrible DA (<10)/Keywords (50>)', label: 'Horrible DA (<10) / Keywords (50>)' },
  { value: 'Low DA (<20)/Keywords (250+)', label: 'Low DA (<20) / Keywords (250+)' },
  { value: 'Middle DA (20-30)/Keywords (500+)', label: 'Middle DA (20-30) / Keywords (500+)' },
  { value: 'Middle-High DA (30-40)/Keywords (1,000+)', label: 'Middle-High DA (30-40) / Keywords (1,000+)' },
  { value: 'High DA (40+)/Keywords (5,000+)', label: 'High DA (40+) / Keywords (5,000+)' },
]

// X-axis options (Industry/Request)
export const industryOptions: { value: LeadScoreIndustry; label: string }[] = [
  { value: 'Odd Business/Odd Request', label: 'Odd Business / Odd Request' },
  { value: 'Good Industry/Odd Request', label: 'Good Industry / Odd Request' },
  { value: 'Odd Industry/Good Request', label: 'Odd Industry / Good Request' },
  { value: 'Good Industry/Good Request', label: 'Good Industry / Good Request' },
]

// Scoring matrix: [daIndex][industryIndex] = score
// DA rows: Horrible(0), Low(1), Middle(2), Middle-High(3), High(4)
// Industry cols: Odd/Odd(0), Good/Odd(1), Odd/Good(2), Good/Good(3)
const scoreMatrix: number[][] = [
  [1, 1, 1, 2], // Horrible
  [1, 2, 2, 3], // Low
  [2, 3, 3, 4], // Middle
  [3, 4, 4, 5], // Middle-High
  [4, 5, 5, 5], // High
]

export const calculateLeadScore = (
  da: LeadScoreDA | undefined,
  industry: LeadScoreIndustry | undefined
): number | undefined => {
  if (!da || !industry) return undefined
  const daIndex = daOptions.findIndex(o => o.value === da)
  const industryIndex = industryOptions.findIndex(o => o.value === industry)
  if (daIndex === -1 || industryIndex === -1) return undefined
  return scoreMatrix[daIndex][industryIndex]
}

const getScoreColor = (score: number | undefined) => {
  if (score === undefined) return 'text.secondary'
  if (score <= 1) return 'error.main'
  if (score === 2) return 'warning.main'
  if (score === 3) return 'info.main'
  if (score === 4) return 'success.light'
  return 'success.main'
}

interface EditLeadScoreCellProps {
  params: GridRenderEditCellParams
}

const EditLeadScoreCell = ({ params }: EditLeadScoreCellProps) => {
  const theme = useTheme()
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null)
  const [step, setStep] = useState<'da' | 'industry'>('da')
  const [selectedDA, setSelectedDA] = useState<LeadScoreDA | undefined>(params.row.leadScoreDA)
  const [open, setOpen] = useState(false)

  // Use callback ref to capture anchor element and open popover
  const setAnchorRef = (node: HTMLDivElement | null) => {
    if (node && !anchorEl) {
      setAnchorEl(node)
      setOpen(true)
    }
  }

  useEffect(() => {
    // If DA is already selected, go straight to industry step
    if (params.row.leadScoreDA && !params.row.leadScoreIndustry) {
      setSelectedDA(params.row.leadScoreDA)
      setStep('industry')
    }
  }, [params.row.leadScoreDA, params.row.leadScoreIndustry])

  const handleClose = () => {
    setOpen(false)
    params.api.stopCellEditMode({ id: params.id, field: params.field })
  }

  const handleDASelect = (da: LeadScoreDA) => {
    setSelectedDA(da)
    setStep('industry')
  }

  const handleIndustrySelect = (industry: LeadScoreIndustry) => {
    const score = calculateLeadScore(selectedDA, industry)

    // Update all three fields
    params.api.setEditCellValue({
      id: params.id,
      field: 'leadScoreDA',
      value: selectedDA,
    })
    params.api.setEditCellValue({
      id: params.id,
      field: 'leadScoreIndustry',
      value: industry,
    })
    params.api.setEditCellValue({
      id: params.id,
      field: 'leadScore',
      value: score,
    })

    // Update the main field value and exit edit mode
    params.api.setEditCellValue({
      id: params.id,
      field: params.field,
      value: score,
    })
    setOpen(false)
    params.api.stopCellEditMode({ id: params.id, field: params.field })
  }

  return (
    <Box ref={setAnchorRef} sx={{ width: '100%', height: '100%' }}>
      <Popover
        open={open && Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              minWidth: 280,
              bgcolor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
            },
          },
        }}
      >
        <Box sx={{ p: 1.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Typography variant="subtitle2" color="text.secondary">
            {step === 'da' ? 'Step 1: Select DA/Keywords' : 'Step 2: Select Industry/Request'}
          </Typography>
          {step === 'industry' && selectedDA && (
            <Typography variant="caption" color="primary.main" sx={{ display: 'block', mt: 0.5 }}>
              DA: {daOptions.find(o => o.value === selectedDA)?.label}
            </Typography>
          )}
        </Box>

        <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
          {step === 'da'
            ? daOptions.map(option => (
                <MenuItem
                  key={option.value}
                  onClick={() => handleDASelect(option.value)}
                  sx={{
                    py: 1,
                    '&:hover': { bgcolor: theme.palette.action.hover },
                  }}
                >
                  <Typography variant="body2">{option.label}</Typography>
                </MenuItem>
              ))
            : industryOptions.map(option => {
                const previewScore = calculateLeadScore(selectedDA, option.value)
                return (
                  <MenuItem
                    key={option.value}
                    onClick={() => handleIndustrySelect(option.value)}
                    sx={{
                      py: 1,
                      display: 'flex',
                      justifyContent: 'space-between',
                      '&:hover': { bgcolor: theme.palette.action.hover },
                    }}
                  >
                    <Typography variant="body2">{option.label}</Typography>
                    <Box
                      sx={{
                        ml: 2,
                        minWidth: 24,
                        height: 24,
                        borderRadius: '50%',
                        bgcolor: getScoreColor(previewScore),
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold',
                        fontSize: 12,
                      }}
                    >
                      {previewScore}
                    </Box>
                  </MenuItem>
                )
              })}
        </Box>

        {step === 'industry' && (
          <Box
            sx={{
              p: 1,
              borderTop: `1px solid ${theme.palette.divider}`,
              display: 'flex',
              justifyContent: 'flex-start',
            }}
          >
            <Typography
              variant="caption"
              sx={{ cursor: 'pointer', color: 'primary.main' }}
              onClick={() => setStep('da')}
            >
              ← Back to DA selection
            </Typography>
          </Box>
        )}
      </Popover>
    </Box>
  )
}

export default EditLeadScoreCell
